// Client-specific configuration for the Veralta Health clinical demo — the
// same templatization seam apps/demo/src/scenario/torbridge.ts is, trimmed to
// what a live-only app needs (no recorded layers). Components read from this
// object and never hard-code client names or narrative text.

import type { LivePanelCopy } from "@demo/ui/LivePanel.js";
import type { HeroConfig } from "@demo/ui/HeroPanel.js";
import type { FlowConfig } from "@demo/ui/ProcessFlow.js";

export interface VeraltaScenario {
  client: { name: string; domain: string; mark: string };
  product: { name: string; tagline: string };
  disclosure: string;
  masthead: string;
  intro: { headline: string; body: string };
  // Overview tab: the two-card verify hero + the process strip. Card bundleIds
  // key into CLINIC_BUNDLES (apps/clinic-demo/src/data.ts).
  hero: HeroConfig;
  flow: FlowConfig;
  liveCta: { label: string; sub: string };
  live: LivePanelCopy;
}

export const VERALTA: VeraltaScenario = {
  client: {
    name: "Veralta Health",
    domain: "veraltahealth.com",
    mark: "V"
  },
  product: {
    name: "Sequesign",
    tagline: "Verifiable receipts for agent actions"
  },
  disclosure:
    "Fictional demonstration. Veralta Health, its patients, and its providers are not real. The records shown are invented and no PHI is sealed: only a hash of the record and of each cited span is recorded. Verification is performed live in your browser.",
  masthead: "Agent Actions, Verified",
  hero: {
    eyebrow: "See it in 15 seconds",
    headline: "A clinical agent submitted these prior auths. Trust it, or verify it?",
    sub: "Veralta's clinical agent reviewed a patient record and submitted prior authorizations under a plan that only allows in-network payers. No PHI is sealed, only a hash of the record. One went to an allowed payer; the other to a payer off the allowlist. Each run is sealed into a receipt you recompute yourself, right here in your browser. Check them:",
    verifyLabel: "Verify this receipt",
    verifyingLabel: "Verifying…",
    reverifyLabel: "Verify again",
    browserNote:
      "Each check is recomputed with the real Sequesign verifier, right here in your browser. Nothing is sent anywhere.",
    cards: {
      conformant: {
        bundleId: "prior-auth-conformant",
        tag: "On allowlist",
        amount: "Veralta Health Plan",
        headline: "Prior auth submitted",
        line: "Payer on the plan's allowlist: the agent reviewed the record and submitted the authorization, then sealed the receipt.",
        expect:
          "You should expect every signature, hash, and the bound mandate to check out, and the submission to conform to the allowlist."
      },
      violation: {
        bundleId: "prior-auth-violation",
        tag: "Off allowlist",
        amount: "Northwind Health",
        headline: "Submitted to Northwind Health",
        line: "The same agent, but a payer off the plan's allowlist: it submitted anyway, and sealed the receipt.",
        expect:
          "The receipt is just as valid, but it records the off-allowlist submission honestly as conformant: false."
      }
    }
  },
  flow: {
    seqLabel: "The Sequesign layer",
    steps: [
      { icon: "invoice", label: "Record arrives", sub: "A patient record enters the intake queue" },
      { icon: "inbox", label: "Intake", sub: "referral-coordinator@veralta" },
      {
        icon: "agent",
        label: "AI agent",
        sub: "Reviews the record and decides: the record is only hashed, never sealed"
      },
      {
        icon: "seal",
        label: "Sealed receipt",
        sub: "The decision is cryptographically sealed",
        seq: true
      },
      {
        icon: "verify",
        label: "Anyone verifies",
        sub: "Checked offline, in any browser",
        seq: true,
        verify: true
      }
    ]
  },
  liveCta: {
    label: "Run a real seal with your own keys",
    sub: "Presenter mode: run a live clinical session against the real witness and seal a brand-new receipt end to end."
  },
  intro: {
    headline: "Every clinical agent action, provable.",
    body: "Veralta's clinical agent works two jobs: submitting a prior authorization and scanning a patient record for a condition. Pick a job, review the editable record, and run it: the agent seals a real Sequesign receipt server-side, then you verify it right here in your browser. The record itself is never sealed, only a hash of it is, so the receipt binds the exact record without carrying PHI."
  },
  live: {
    title: "Live run: real seal, real witness, real review",
    intro: [
      "Pick a clinical job and review the editable patient record. On Run, the agent executes a real Sequesign session with your own agent key, makes one real Claude call, gets each action countersigned by a real witness, and seals the package, then verifies it here in your browser.",
      "The result is verified in EXTERNAL trust-anchor mode: the witness's published keys are fetched from its well-known endpoint (directly, or relayed unchanged through this site) and used as the trust anchor, so this is an independent third-party check, not an integrity self-check.",
      "What is sealed is the agent's SUBSTANCE (the decision, the payer and codes, the grounded findings) plus a hash of the record, of each cited location and span, and of the prompt and instruction. The patient record text, the model prompt, the operator instruction, any justification text, and every cited location are never sealed in the clear: only their hashes. Push a job past its mandate (an off-allowlist payer, or an out-of-scope finding) and the sealed receipt honestly records conformant: false. It still verifies as authentic; the violation is recorded, not hidden."
    ],
    contactNote:
      "Live mode contacts the real witness and the real Anthropic API using the keys you enter below. Nothing else on this page makes a network call to those services.",
    keyNote:
      "Keys are used only for this run, in your browser and the request; nothing is stored. They live in React state only, are never written to localStorage, and are cleared when you leave the page.",
    runLabel: "Run live",
    runningLabel: "Running live seal…",
    fields: {
      agentPem: "Agent private key (Ed25519 PEM)",
      witnessBaseUrl: "Witness base URL",
      witnessApiKey: "Witness API key (optional)",
      llmApiKey: "Anthropic API key"
    },
    reviewInstruction: {
      label: "Agent instruction (optional)",
      placeholder: "e.g. Only submit if first-line therapy is documented as failed.",
      help: "Prepended to the agent's prompt. Only a HASH of it is sealed into the receipt (the instruction is committed and tamper-evident, but its text is not stored), so it stays part of the record without ever carrying PHI."
    }
  }
};
